import { FormInputProps } from '@form/props';
import { faBan, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import useModalManager from '@hooks/useModalManager';
import { Button, CircularProgress, Spinner } from '@ui';
import { useToastStore } from 'components/ToastManager/useToasts';
import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';
import { cx } from 'class-variance-authority';
import { floor } from 'lodash';
import moment from 'moment';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const ImageHandler = dynamic(() => import('components/Form/next/FormSingleDropzone/ImageHandler'));

interface Props extends FormInputProps, HTMLAttributes<HTMLDivElement> {
    options?: DropzoneOptions;
    disableCropper?: boolean;
    cropperOptions?: Cropper.GetCroppedCanvasOptions;
    aspectRatio?: number;
    image?: boolean;
    imageUsage?: string;
}

interface FileModel {
    mime_type: string;
    name: string;
    id?: number;
    url?: string;
    lastModified: string | number;
}

const FormSingleDropzone = ({
    name,
    label,
    options,
    disableCropper,
    cropperOptions,
    aspectRatio,
    className,
    image,
    imageUsage,
    ...props
}: Props) => {
    const { t } = useTranslation('form');
    const resolvedAspectRatio = aspectRatio ?? 1;
    const { control } = useFormContext();
    const {
        field: { value, onChange }
    } = useController({ name, control });
    const [file, setFile] = useState<FileModel>(null);
    const [uploadStatus, setUploadStatus] = useState(file && 'id' in file ? 100 : 0);
    const [uploadError, setUploadError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!value);
    const isUploaded = file && 'id' in file && uploadStatus === 100;
    const { addModal, removeModal } = useModalManager();

    const uploadCancelToken = useRef<CancelTokenSource>(null);

    const uploadFile = useCallback(
        (blob: Blob) => {
            if (!isUploaded) {
                setUploadError(null);
                uploadCancelToken.current = axios.CancelToken.source();
                let data = new FormData();
                data.append('file', blob);

                if (imageUsage) {
                    data.append('usage', imageUsage);
                }

                axios
                    .post(`${process.env.NEXT_PUBLIC_API_HOST}${image ? 'image' : 'file'}/upload`, data, {
                        cancelToken: uploadCancelToken.current.token,
                        onUploadProgress: (e: AxiosProgressEvent) => {
                            let status = floor((e.loaded / e.total) * 100);
                            status = status > 0 ? status - 1 : status;
                            setUploadStatus(status);
                        }
                    })
                    .then(({ data }) => {
                        uploadCancelToken.current = null;
                        setUploadStatus(100);
                        setFile((file) => ({ ...data.data, ...file }));
                        onChange(data.data.id);
                    })
                    .catch((data) => {
                        setUploadError(data.response?.data);
                        setUploadStatus(-1);
                    });

                return uploadCancelToken.current;
            }
        },
        [isUploaded, setFile, uploadStatus, imageUsage, image]
    );

    const openImageHandler = useCallback(
        (blob: Blob) => {
            addModal({
                title: 'Bild bearbeiten',
                content: (id) => (
                    <ImageHandler
                        cropOptions={cropperOptions}
                        aspectRatio={aspectRatio}
                        image={blob}
                        onCrop={(croppedBlob) => {
                            uploadFile(croppedBlob);
                            removeModal(id);
                        }}
                    />
                ),
                show: true,
                size: 50,
                onClose: () => {
                    uploadCancelToken.current?.cancel();
                    setFile(null);
                    setUploadStatus(0);
                }
            });
        },
        [cropperOptions, aspectRatio, uploadFile]
    );

    const handleDeleteOrCancel = useConfirmedCallback((fileId?: number) => {
        if (uploadCancelToken.current) {
            uploadCancelToken.current.cancel();
            setFile(null);
            setUploadStatus(0);
        } else if (fileId) {
            setIsDeleting(true);
            const clear = () => {
                setIsDeleting(false);
                setFile(null);
                onChange(undefined);
                setUploadStatus(0);
            };
            axios
                .delete(`${process.env.NEXT_PUBLIC_API_HOST}${image ? 'image' : 'file'}/${fileId}`)
                .then(clear, (error) => {
                    // guests may not delete on the server; the orphaned upload just stays behind
                    if (error.response?.status === 401) return clear();

                    setIsDeleting(false);
                    useToastStore.getState().addToast({ type: 'error', title: t('dropzone.deleteFailed') });
                });
        } else {
            setIsDeleting(false);
            setFile(null);
            onChange(undefined);
            setUploadStatus(0);
        }
    });

    const fetchFileData = useCallback(
        (fileId: number) => {
            axios
                .get(`${process.env.NEXT_PUBLIC_API_HOST}${image ? 'image' : 'file'}/view/${fileId}`)
                .then(({ data }) => {
                    setUploadStatus(100);
                    setFile((file) => ({ ...data.data, ...file }));
                    setIsLoading(false);
                })
                // A deleted or unreachable file must still end the loading state, otherwise
                // the field shows a spinner that never resolves.
                .catch(() => setIsLoading(false));
        },
        [setFile, setUploadStatus, setIsLoading, image]
    );

    const handleUpload = useCallback(
        (blob: Blob) => {
            if (!disableCropper && image && blob.type.startsWith('image')) openImageHandler(blob);
            else uploadFile(blob);
        },
        [disableCropper, image, openImageHandler, uploadFile]
    );

    const handleViewFileInTab = useCallback(() => {
        if (window && file.url) window.open(file.url, '_blank');
    }, [file]);

    const handleViewFile = useCallback(() => {
        if (file.url)
            addModal({
                title: `Datei ${file.id ?? file.name}`,
                content: (
                    <div className="tw-overflow-hidden tw-rounded-xl" onClick={handleViewFileInTab}>
                        <Image
                            src={file.url}
                            layout="responsive"
                            width={resolvedAspectRatio * 200}
                            height={200}
                            alt={file.name}
                        />
                    </div>
                ),
                show: true,
                size: 60
            });
    }, [file, handleViewFileInTab, resolvedAspectRatio]);

    const onDrop = useCallback(
        (acceptedFiles: any) => {
            if (file) return;
            if (acceptedFiles.length === 0) return;
            let newFile: File = acceptedFiles[0];
            setFile({
                name: newFile.name,
                mime_type: newFile.type,
                lastModified: newFile.lastModified
            });
            handleUpload(newFile);
        },
        [handleUpload, file]
    );
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        multiple: false,
        ...options,
        accept:
            options?.accept ??
            (image ? ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/jfif'] : undefined)
    });

    // Loads the stored file behind the field value. An empty value (including the `[]` a
    // list-shaped default leaves behind) means "no file" — fetching it would 404 and the
    // spinner started by `useState(!!value)` would never stop.
    useEffect(() => {
        if (file !== null) return;

        const fileId = Array.isArray(value) ? value[0] : value;

        if (!fileId) {
            setIsLoading(false);
            return;
        }

        fetchFileData(fileId);
    }, [value, fetchFileData]);

    return (
        <div className={cx('tw-my-2', className)} {...props}>
            {label && <label htmlFor={`${name}-dropzone`}>{label}</label>}
            <div
                {...getRootProps()}
                id={`${name}-dropzone`}
                className={cx(
                    'tw-card-flat tw-mb-2 tw-flex tw-min-h-[100px] tw-cursor-pointer tw-items-center',
                    uploadError ? 'tw-bg-red-200 tw-text-red-700' : null
                )}
            >
                <input {...getInputProps({ disabled: !!file })} />
                {isLoading ? (
                    <div className="tw-w-full tw-flex-1">
                        <Spinner size="sm" color="currentColor" />
                    </div>
                ) : uploadError ? (
                    uploadError.message
                ) : file !== null ? (
                    <div className="tw-flex tw-h-full tw-flex-1 tw-items-center tw-gap-8">
                        <div className="tw-flex tw-flex-1 tw-flex-col">
                            <span className="tw-text-lg">{file.name}</span>
                            <span className="tw-text-xs tw-text-accents-7">
                                {moment(file.lastModified).format('lll')}
                            </span>
                        </div>
                        {!isUploaded ? (
                            <CircularProgress progress={uploadStatus} radius={30} withoutText />
                        ) : image ? (
                            <div
                                onClick={handleViewFile}
                                className="tw-relative tw-h-16 tw-self-start tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-accents-5"
                                style={{ aspectRatio: String(resolvedAspectRatio) }}
                            >
                                <Image
                                    src={file.url}
                                    layout="responsive"
                                    width={resolvedAspectRatio * 100}
                                    height={100}
                                    alt={file.name}
                                />
                            </div>
                        ) : null}
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <Button
                                isLoading={isDeleting}
                                color="error"
                                size="sm"
                                onPress={() => handleDeleteOrCancel('id' in file ? file.id : undefined)}
                            >
                                <FontAwesomeIcon icon={isUploaded ? faTrash : faBan} />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <span className="tw-w-full tw-overflow-hidden tw-overflow-ellipsis tw-p-6 tw-text-center tw-text-accents-8">
                        {t('dropzone.promptSingle')}
                    </span>
                )}
            </div>
        </div>
    );
};

export default FormSingleDropzone;
